/* eslint-disable import/no-anonymous-default-export */
import type { Theme } from '@mui/material';
import {
    Chip,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import { formatDate } from '@/utils/date_utils';
import {
    RecordContextProvider,
    SimpleListLoading,
    useListContext,
} from 'react-admin';
import { Link } from 'react-router-dom';

import { defaultQuoteStatuses, defaultTypeOfWork } from '@/root/defaultConfiguration';
import { QuoteJobFolder } from '@/types';

const pendingFinishColor =
    defaultQuoteStatuses.find(s => s.value === '3')?.color ?? '#f0ad4e';

const legacyRowSx = {
    bgcolor: 'warning.50',
    borderLeft: 3,
    borderColor: 'warning.main',
    '&:hover': { bgcolor: 'warning.100' },
} as const;

export const QuoteJobFolderListContent = () => {
    const { data: folders, error, isPending } = useListContext<QuoteJobFolder>();
    const isSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('md')
    );

    if (isPending) {
        return <SimpleListLoading hasLeftAvatarOrIcon hasSecondaryText />;
    }
    if (error) {
        return null;
    }

    return (
        <List dense>
            {folders.map(item => (
                <RecordContextProvider key={item.id} value={item}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={`/quotes/folders/${item.id}`}
                            sx={item.has_legacy ? legacyRowSx : undefined}
                        >
                            <ListItemText
                                primary={
                                    <Typography
                                        component="span"
                                        variant="body1"
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                                    >
                                        {item.has_legacy && (
                                            <Chip label="Legacy" size="small" color="warning" />
                                        )}
                                        {item.name}
                                        {item.has_pending_finish_quoting && (
                                            <Tooltip title="Pending finish quoting" placement="top">
                                                <FlagIcon
                                                    sx={{ color: pendingFinishColor, fontSize: 18 }}
                                                    aria-label="Pending finish quoting"
                                                />
                                            </Tooltip>
                                        )}
                                        {item.is_estimate_sent && (
                                            <Tooltip title="Estimate sent" placement="top">
                                                <FlagIcon
                                                    sx={{ color: 'info.main', fontSize: 18 }}
                                                    aria-label="Estimate sent"
                                                />
                                            </Tooltip>
                                        )}
                                    </Typography>
                                }
                                secondary={
                                    item.type_of_work
                                        ? defaultTypeOfWork[item.type_of_work] ??
                                          item.type_of_work
                                        : undefined
                                }
                            />
                            <ListItemText
                                primary={`${item.quote_count} quote${item.quote_count === 1 ? '' : 's'}`}
                                secondary={
                                    [
                                        item.creator_name,
                                        item.contractor_name,
                                        item.property_owner_name &&
                                        item.property_owner_name !== item.contractor_name
                                            ? `Owner: ${item.property_owner_name}`
                                            : null,
                                    ]
                                        .filter(Boolean)
                                        .join(' · ') || undefined
                                }
                                sx={{ textAlign: isSmall ? 'left' : 'right', maxWidth: 220 }}
                            />
                            {!isSmall && item.created_at && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    title={item.created_at}
                                    sx={{
                                        minWidth: 120,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                        lineHeight: 1.25,
                                    }}
                                >
                                    <span>{formatDate(item.created_at, 'MM/dd/yyyy')}</span>
                                    <span>{formatDate(item.created_at, 'HH:mm')}</span>
                                </Typography>
                            )}
                        </ListItemButton>
                    </ListItem>
                </RecordContextProvider>
            ))}

            {folders.length === 0 && (
                <ListItem>
                    <ListItemText primary="No quote job folders found" />
                </ListItem>
            )}
        </List>
    );
};
